<template>
  <div class="tslider">
    <div ref="scale" class="tslider-scale" @mousedown="onDown" @touchstart.prevent="onDown">
      <!-- ruler numbers (every 10) -->
      <div class="tslider-num" v-for="n in majors" :key="'n' + n" :style="{ left: n + '%' }">{{ n }}</div>
      <!-- ruler ticks (every unit, taller every 10) -->
      <div
        v-for="t in ticks"
        :key="'t' + t"
        class="tslider-tick"
        :class="{ major: t % 10 === 0, medium: t % 5 === 0 }"
        :style="{ left: t + '%' }"
      />
      <!-- rail + green fill + thumb -->
      <div class="tslider-rail" />
      <div class="tslider-fill" :style="{ width: pct + '%' }" />
      <div class="tslider-thumb" :style="{ left: pct + '%' }" />
    </div>
    <div v-if="label" class="tslider-label">{{ label }}</div>
  </div>
</template>

<script>
// Ruler-style transparency slider shared by the report comparison screens.
// v-model is a 0–100 integer (percentage). Matches the Figma "Transparency Slider".
export default {
  name: "TransparencySlider",
  props: {
    value: { type: Number, default: 50 },
    label: { type: String, default: "" },
  },
  data() {
    return { dragging: false };
  },
  computed: {
    pct() {
      return Math.max(0, Math.min(100, Number(this.value) || 0));
    },
    ticks() {
      return Array.from({ length: 101 }, (_, i) => i);
    },
    majors() {
      return Array.from({ length: 11 }, (_, i) => i * 10);
    },
  },
  beforeDestroy() {
    this.stop();
  },
  methods: {
    clientX(e) {
      return e.touches && e.touches.length ? e.touches[0].clientX : e.clientX;
    },
    onDown(e) {
      this.dragging = true;
      this.setFrom(e);
      window.addEventListener("mousemove", this.onMove);
      window.addEventListener("mouseup", this.stop);
      window.addEventListener("touchmove", this.onMove, { passive: false });
      window.addEventListener("touchend", this.stop);
    },
    onMove(e) {
      if (!this.dragging) return;
      if (e.cancelable) e.preventDefault();
      this.setFrom(e);
    },
    stop() {
      this.dragging = false;
      window.removeEventListener("mousemove", this.onMove);
      window.removeEventListener("mouseup", this.stop);
      window.removeEventListener("touchmove", this.onMove);
      window.removeEventListener("touchend", this.stop);
    },
    setFrom(e) {
      const el = this.$refs.scale;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (!rect.width) return;
      let p = ((this.clientX(e) - rect.left) / rect.width) * 100;
      p = Math.max(0, Math.min(100, Math.round(p)));
      if (p !== this.pct) this.$emit("input", p);
    },
  },
};
</script>

<style scoped>
.tslider {
  padding: 0 14px;
  user-select: none;
}
.tslider-scale {
  position: relative;
  height: 52px;
  cursor: pointer;
}
.tslider-num {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  font-size: 11px;
  color: #9e9e9e;
  pointer-events: none;
}
.tslider-tick {
  position: absolute;
  top: 20px;
  width: 1px;
  height: 5px;
  background: #dcdcdc;
  transform: translateX(-50%);
  pointer-events: none;
}
.tslider-tick.medium {
  height: 8px;
  background: #cfcfcf;
}
.tslider-tick.major {
  height: 12px;
  background: #bdbdbd;
}
.tslider-rail {
  position: absolute;
  top: 38px;
  left: 0;
  right: 0;
  height: 5px;
  border-radius: 3px;
  background: #dcdcdc;
  pointer-events: none;
}
.tslider-fill {
  position: absolute;
  top: 38px;
  left: 0;
  height: 5px;
  border-radius: 3px;
  background: #21c274;
  pointer-events: none;
}
.tslider-thumb {
  position: absolute;
  top: 40px;
  transform: translate(-50%, -50%);
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #21c274;
  box-shadow: 0 0 0 6px rgba(33, 194, 116, 0.18);
  pointer-events: none;
}
.tslider-label {
  margin-top: 10px;
  text-align: center;
  font-weight: 600;
  color: #212121;
}
</style>
